import React, { useState, useContext } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Lock,
  CreditCard,
  User,
  MapPin,
  Hash,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  HelpCircle,
} from "lucide-react";
import posthog from "posthog-js";

import { MainStore } from "../../stores/Main";
import FreedeLogo from "../../assets/svg/logo-dark.svg";
import TestLogo from "../../assets/svg/logo-testagency.svg";
import TestBLogo from "../../assets/svg/logo-testagencyb.svg";
import KFLogo from "../../assets/svg/logo-knutefinancial.svg";
import PoweredBy from "../../utils/poweredBy";
import PoweredByImg from "../../assets/svg/powered-by.svg";
import DebtDisclosure from "../../components/debt-disclosure/debt-disclosure";
import DisclosureModal from "../../components/disclosure-modal/disclosure-modal";
import CloudsImg from "../../assets/images/clouds.png";
import api from "../../utils/api";
import { 
  trackQuickAccessLoginSuccess, 
  trackQuickAccessLoginFailed 
} from "../../utils/posthogTracking";

// ------------------------------------------------------
// UI COMPONENTS (matching landing page)
// ------------------------------------------------------
function CTA({
  href,
  to,
  children,
  variant = "primary",
  className = "",
  onClick,
  type = "button",
  disabled = false,
  loading = false,
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const styles =
    variant === "primary"
      ? "bg-blue-600 hover:bg-blue-500 active:scale-[.98] text-white shadow-[0_10px_30px_rgba(37,99,235,0.35)] focus-visible:ring-blue-300"
      : variant === "secondary"
        ? "bg-white/10 hover:bg-white/20 active:scale-[.98] text-white backdrop-blur-sm border border-white/15 focus-visible:ring-white/40"
        : "bg-transparent hover:bg-white/10 text-white border border-white/20";

  const Component = to ? Link : href ? "a" : "button";
  const props = to ? { to } : href ? { href } : { type, onClick };

  return (
    <Component
      {...props}
      disabled={disabled || loading}
      className={`${base} ${styles} ${className}`}
    >
      {loading ? (
        <>
          <svg
            className="animate-spin h-4 w-4 text-current"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span>Processing...</span>
        </>
      ) : (
        children
      )}
    </Component>
  );
}

function Brand({ logoPath, logoHeight, logoWidth, agencyName }) {
  return (
    <Link
      to="/"
      className="flex items-center gap-2 select-none"
      aria-label={agencyName || "Home"}
    >
      <img
        src={logoPath}
        alt={agencyName || "Home"}
        height={logoHeight}
        width={logoWidth}
        className="drop-shadow-sm"
        draggable={false}
      />
      <span className="sr-only">{agencyName || "Home"}</span>
    </Link>
  );
}

function FormInput({
  id,
  placeholder,
  type = "text",
  value,
  onChange,
  onKeyDown,
  disabled = false,
  error = false,
  autoFocus = false,
  maxLength,
  icon: Icon,
}) {
  return (
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-4 w-4 text-white/40" />
        </div>
      )}
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
        maxLength={maxLength}
        className={`
          w-full 
          rounded-xl 
          border 
          bg-white/10 
          px-4 
          py-3 
          text-white 
          placeholder:text-white/50 
          backdrop-blur-sm 
          transition-all 
          focus:outline-none 
          focus:ring-2 
          focus:ring-blue-400/50 
          focus:border-transparent
          disabled:opacity-50 
          disabled:cursor-not-allowed
          ${Icon ? "pl-10" : ""}
          ${error ? "border-red-400/50 focus:ring-red-400/50" : "border-white/20 hover:border-white/30"}
        `}
      />
    </div>
  );
}

// Tooltip component
function Tooltip({ children }) {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div className="absolute z-10 -top-2 left-6 transform -translate-y-full">
          <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
            <div className="font-medium mb-1">Where can I find this?</div>
            <div className="text-white/80">
              You can find your reference number on any
              <br />
              written communication from our office.
            </div>
            <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------
// PAGE COMPONENT
// ------------------------------------------------------
const QuickAccessPage = () => {
  const mainStore = useContext(MainStore);
  const agencyDetails = mainStore.agencyDetails || {};
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    referenceNumber: "",
    firstName: "",
    lastName: "",
    zipCode: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Dynamic logo selection based on hostname
  let logoPath = FreedeLogo;
  let logoHeight = "41";
  let logoWidth = "159";

  if (window.location.host.includes("testagencyb")) {
    logoPath = TestBLogo;
  } else if (
    window.location.host.includes("testagency") ||
    window.location.host.includes("freede-agency")
  ) {
    logoPath = TestLogo;
  } else if (window.location.host.includes("knute")) {
    logoPath = KFLogo;
    logoHeight = "32";
    logoWidth = "120";
  }

  // Handle input changes
  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.referenceNumber.trim()) {
      newErrors.referenceNumber = "Reference number is required";
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = "First name must be at least 2 characters";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = "Last name must be at least 2 characters";
    }

    if (!formData.zipCode.trim()) {
      newErrors.zipCode = "Zip code is required";
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode.trim())) {
      newErrors.zipCode = "Please enter a valid zip code";
    }

    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/quick-access/auth", {
        first: formData.firstName.trim(),
        last: formData.lastName.trim(),
        ref: formData.referenceNumber.trim(),
        zip: formData.zipCode.trim(),
      });

      // If authentication successful, check if user is logged in
      if (response.data && !response.data.error) {
        // First identify the user in PostHog with their reference number
        // This should happen before any success events to ensure proper association
        const userId = formData.referenceNumber.trim();
        
        // Use alias to associate the anonymous user with the identified user
        posthog?.alias(userId);
        
        // Then identify the user
        posthog?.identify(userId);
        
        // Now track successful login event (after identification)
        trackQuickAccessLoginSuccess(userId);
        
        // Store authentication success
        mainStore.handleLoginSuccess({
          agency: response.data.agency,
          questionnaire: response.data.questionnaire,
          phoneVerified: response.data.phone_verified,
          agencyLinked: response.data.agency_linked,
          sentimentQuestionnaire: response.data.sentiment_questionnaire,
          userType: "consumer-quick-access",
        });

        // Add a small delay before redirect to ensure PostHog can process the identify call
        setTimeout(() => {
          window.location.href = "/dashboard/disclosures";
        }, 100);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Quick access auth submit failed", err);

      // Surface the server's reason when it sends one (the Freede API returns
      // a distinct {message} on a 401 — e.g. "not eligible — contact us",
      // "No accounts available…"). Fall back to the generic copy otherwise.
      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Authentication failed. Please check your information and try again.";

      // Track failed login attempt to PostHog
      const fieldsWithErrors = ["referenceNumber", "firstName", "lastName", "zipCode"];
      trackQuickAccessLoginFailed({
        errorType: "authentication_failed",
        fieldsWithErrors: fieldsWithErrors,
        errorMessage: serverMessage,
      });

      setErrors({
        general: serverMessage,
        referenceNumber: true,
        firstName: true,
        lastName: true,
        zipCode: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen text-white bg-gradient-to-b from-[#071427] via-[#071427] to-[#06111f] relative overflow-hidden">
        {/* Background orbs */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-blue-500/30 blur-3xl"
        />

        {/* Cloud background image - faded */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: `url(${CloudsImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />

        {/* Header */}
        <header className="relative z-10">
          <div className="mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
            <Brand
              logoPath={logoPath}
              logoHeight={logoHeight}
              logoWidth={logoWidth}
              agencyName={agencyDetails.name}
            />
            <div className="flex items-center gap-2 text-xs text-white/60">
              <ShieldCheck className="h-4 w-4" />
              <span className="hidden sm:inline">Secure & encrypted</span>
            </div>
          </div>
        </header>

        {/* Success Content */}
        <section className="relative z-10 mt-20">
          <div className="mx-auto max-w-2xl px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="rounded-3xl border border-white/10 bg-white/[.06] p-12 shadow-2xl backdrop-blur-sm text-center"
            >
              <div className="mx-auto w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                <ShieldCheck className="h-10 w-10 text-green-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">
                Authentication Successful!
              </h2>
              <p className="text-white/70 mb-8 max-w-md mx-auto">
                Your information has been verified. You'll be redirected to
                complete your payment shortly.
              </p>
              <p className="text-sm text-white/50">
                (This is a demo - backend integration coming soon)
              </p>
              <div className="mt-8">
                <CTA to="/" variant="secondary">
                  <ChevronLeft className="h-4 w-4" />
                  Return to Home
                </CTA>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white bg-gradient-to-b from-[#071427] via-[#071427] to-[#06111f] relative overflow-hidden">
      {/* Background orbs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-blue-500/30 blur-3xl"
      />

      {/* Cloud background image - faded */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: `url(${CloudsImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Header */}
      <header className="relative z-10">
        <div className="mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
          <Brand
            logoPath={logoPath}
            logoHeight={logoHeight}
            logoWidth={logoWidth}
            agencyName={agencyDetails.name}
          />
        </div>
      </header>

      {/* Main Content */}
      <section className="relative z-10">
        <div className="mx-auto max-w-6xl px-6 pt-6 md:pt-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Left side - Heading and description */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col justify-center"
            >
              <h1 className="text-4xl font-extrabold tracking-tight md:text-5xl">
                Quick & Secure{" "}
                <span className="text-blue-300">Account Access</span>
              </h1>
              <p className="mt-4 max-w-xl text-white/75">
                Authenticate securely to access your account, view balances,
                make payments, and explore resolution options. Your information
                is encrypted and protected at every step.
              </p>

              {/* Trust indicators */}
              <div className="mt-6 flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-sm text-white/60">
                  <Lock className="h-4 w-4" />
                  <span>256-bit encryption</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-white/60">
                  <CreditCard className="h-4 w-4" />
                  <span>Secure payment processing</span>
                </div>
              </div>

              {/* Steps */}
              <div className="mt-8 space-y-3">
                <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider">
                  How it works
                </h3>
                <ol className="space-y-2 text-white/70 text-sm">
                  <li className="flex gap-3">
                    <span className="mt-0.5 h-5 w-5 grid place-items-center rounded-full bg-blue-600 text-white text-xs flex-shrink-0">
                      1
                    </span>
                    Enter your consumer reference number and personal details
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 h-5 w-5 grid place-items-center rounded-full bg-blue-600 text-white text-xs flex-shrink-0">
                      2
                    </span>
                    We verify your identity securely
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 h-5 w-5 grid place-items-center rounded-full bg-blue-600 text-white text-xs flex-shrink-0">
                      3
                    </span>
                    View your balance, payment options, and manage your account
                  </li>
                  {agencyDetails?.base_chat_domain && (
                    <li className="flex gap-3">
                      <span className="mt-0.5 h-5 w-5 grid place-items-center rounded-full bg-blue-600 text-white text-xs flex-shrink-0">
                        4
                      </span>
                      Get answers anytime from our AI-powered assistant,
                      available 24/7 for quick, secure support
                    </li>
                  )}
                </ol>
              </div>
            </motion.div>

            {/* Right side - Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex flex-col justify-center"
            >
              <div className="rounded-3xl border border-white/10 bg-white/[.06] p-8 shadow-2xl backdrop-blur-sm">
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-500/20 rounded-xl">
                      <CreditCard className="h-6 w-6 text-blue-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">
                      Quick Access Authentication
                    </h2>
                  </div>
                  <p className="text-white/70 text-sm">
                    Enter your information to securely access your account
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Reference Number */}
                  <div>
                    <label
                      htmlFor="referenceNumber"
                      className="flex items-center gap-2 text-sm font-medium text-white/80 mb-2"
                    >
                      Consumer Reference Number
                      <Tooltip>
                        <HelpCircle className="h-3.5 w-3.5 text-white/40 hover:text-white/60 cursor-help transition-colors" />
                      </Tooltip>
                    </label>
                    <FormInput
                      id="referenceNumber"
                      type="text"
                      placeholder="Enter your reference number"
                      value={formData.referenceNumber}
                      onChange={handleChange("referenceNumber")}
                      onKeyDown={handleKeyDown}
                      error={!!errors.referenceNumber}
                      disabled={loading}
                      autoFocus
                      icon={Hash}
                    />
                    {errors.referenceNumber && (
                      <p className="mt-1 text-xs text-red-400">
                        {errors.referenceNumber}
                      </p>
                    )}
                  </div>

                  {/* First Name and Last Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-white/80 mb-2"
                      >
                        First Name
                      </label>
                      <FormInput
                        id="firstName"
                        type="text"
                        placeholder="John"
                        value={formData.firstName}
                        onChange={handleChange("firstName")}
                        onKeyDown={handleKeyDown}
                        error={!!errors.firstName}
                        disabled={loading}
                        icon={User}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-xs text-red-400">
                          {errors.firstName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-white/80 mb-2"
                      >
                        Last Name
                      </label>
                      <FormInput
                        id="lastName"
                        type="text"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={handleChange("lastName")}
                        onKeyDown={handleKeyDown}
                        error={!!errors.lastName}
                        disabled={loading}
                        icon={User}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-xs text-red-400">
                          {errors.lastName}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Zip Code */}
                  <div>
                    <label
                      htmlFor="zipCode"
                      className="block text-sm font-medium text-white/80 mb-2"
                    >
                      Zip Code
                    </label>
                    <FormInput
                      id="zipCode"
                      type="text"
                      placeholder="12345"
                      value={formData.zipCode}
                      onChange={handleChange("zipCode")}
                      onKeyDown={handleKeyDown}
                      error={!!errors.zipCode}
                      disabled={loading}
                      maxLength="10"
                      icon={MapPin}
                    />
                    {errors.zipCode && (
                      <p className="mt-1 text-xs text-red-400">
                        {errors.zipCode}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <CTA
                      type="submit"
                      variant="primary"
                      className="w-full"
                      disabled={loading}
                      loading={loading}
                    >
                      {!loading && (
                        <>
                          <Lock className="h-4 w-4" />
                          Access Your Account
                          <ChevronRight className="h-4 w-4" />
                        </>
                      )}
                    </CTA>

                    {/* General Error Message */}
                    {errors.general && (
                      <p className="mt-3 text-sm text-red-400 text-center">
                        {errors.general}
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 mt-20 pb-12">
        <div className="mx-auto max-w-6xl px-6">
          <div className="py-6 border-t border-white/10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3 text-white/70 text-sm">
                {agencyDetails.name && (
                  <>
                    <span>{agencyDetails.name}</span>
                    <span className="hidden md:inline">•</span>
                    <span>{agencyDetails.phone}</span>
                    <span className="hidden md:inline">•</span>
                    <span>
                      {agencyDetails.address}, {agencyDetails.city}{" "}
                      {agencyDetails.state} {agencyDetails.zip}
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-center">
                {PoweredBy && (
                  <img
                    src={PoweredByImg}
                    alt="Powered by Freede"
                    className="h-5 opacity-80 hover:opacity-100 transition-opacity"
                  />
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-white/60">
              <div className="flex items-center gap-4">
                {mainStore.isAuthenticated ? (
                  <Link
                    to="/dashboard/disclosures"
                    className="hover:text-white"
                  >
                    Consumer Rights & Disclosures
                  </Link>
                ) : (
                  <button
                    onClick={() => setShowDisclosureModal(true)}
                    className="hover:text-white"
                  >
                    Consumer Rights & Disclosures
                  </button>
                )}
                <a
                  className="hover:text-white"
                  href="https://www.freede.co/legal-terms-platform"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Use
                </a>
                <a
                  className="hover:text-white"
                  href="https://www.freede.co/legal-privacy-platform"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
              </div>
              <div className="mt-2 sm:mt-0">
                © {new Date().getFullYear()} Freede Solutions Inc. All rights reserved.
              </div>
            </div>
            <DebtDisclosure agencyDetails={agencyDetails} />
            {agencyDetails?.extra_footer && (
              <div 
                className="mt-3 text-center text-xs text-white/40 max-w-3xl mx-auto px-4"
                dangerouslySetInnerHTML={{ __html: agencyDetails.extra_footer }}
              />
            )}
          </div>
        </div>
      </footer>
      <DisclosureModal 
        isOpen={showDisclosureModal} 
        onClose={() => setShowDisclosureModal(false)} 
      />
    </div>
  );
};

export default QuickAccessPage;
